import { isStandalone } from "./standalone";
import { currentApiScreen } from "./api-screen";

const TOKEN_KEY = "bf_token";

function apiBase(): string {
    const v = import.meta.env.VITE_API_ORIGIN?.trim();
    return v ? v.replace(/\/$/, "") : "";
}

function url(path: string): string {
    const p = path.startsWith("/") ? path : `/${path}`;
    const base = apiBase();
    return base ? `${base}${p}` : p;
}

function authHeaders(): HeadersInit {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The server's own words for a failure, if it gave any.
 *
 *  The API error envelope is `{ error, message, requestId }` and the server
 *  fills `message` in production too (only `stack` is withheld). Until this
 *  landed, `ApiError.message` was the bare status line, so every `toast.error`
 *  in the app read "500" while the actual reason — "value too long for type
 *  character varying(255)" on the booking-conversion 500 — sat unread in
 *  `.body`. A mutation that fails should say why. */
function serverMessage(body: unknown): string | null {
    if (typeof body === "string") {
        const s = body.trim();
        // Plain-text error bodies (non-/api routes) are short; an HTML error
        // page is not a message and must never reach a toast.
        return s && s.length <= 300 && !s.startsWith("<") ? s : null;
    }
    if (body && typeof body === "object") {
        const m = (body as { message?: unknown }).message;
        if (typeof m === "string" && m.trim()) return m.trim();
    }
    return null;
}

export class ApiError extends Error {
    constructor(
        public status: number,
        public statusText: string,
        public body: unknown,
    ) {
        super(serverMessage(body) ?? `${status} ${statusText}`.trim());
        this.name = "ApiError";
    }
    /** Alias for `body` to match a handful of call sites that read `.data`. */
    get data(): unknown {
        return this.body;
    }
    /** The session is gone — not "you may not", which is 403. */
    get isUnauthorized(): boolean {
        return this.status === 401;
    }
    /** Signed in, but not allowed. Must never log anyone out. */
    get isForbidden(): boolean {
        return this.status === 403;
    }
    /** Over the per-person API allowance. Retrying only spends more of it. */
    get isRateLimited(): boolean {
        return this.status === 429;
    }
}

/**
 * The request never came back — it timed out, or the connection dropped
 * mid-flight.
 *
 * Distinct from `ApiError`, which means the SERVER ANSWERED. The two need
 * opposite handling and conflating them is what stranded the app: a 401 means
 * the session is gone and the token must go with it, while a stall means we
 * could not ask at all and the token must be LEFT ALONE. Clearing it on a
 * stall logs a tech out for driving through a dead spot.
 *
 * Extends `TypeError` deliberately: that is what bare `fetch` throws on a
 * network failure, and call sites already branch on `err instanceof TypeError`
 * to say "network error" (e.g. `pages/customers/intakeHelpers.ts`). Widening
 * the type must not silently reclassify their errors as unknown.
 */
export class NetworkError extends TypeError {
    /** True when we gave up on the clock, false when the connection failed. */
    readonly timedOut: boolean;
    constructor(message: string, timedOut: boolean) {
        super(message);
        this.name = "NetworkError";
        this.timedOut = timedOut;
    }
}

/**
 * How long any one request may run before we call it dead.
 *
 * A request with no ceiling never settles, and everything awaiting it hangs
 * too — which is exactly how one stalled `/api/auth/me` left the whole app on
 * a spinner with no error, no retry and no way out but clearing site data.
 *
 * 30s is deliberately generous: a tech on two bars is slow, not broken, and a
 * tight limit would fail work that would have succeeded. Uploads get their own
 * far larger budget below — a job's photos over a weak link legitimately take
 * minutes.
 */
const REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 180_000;

/**
 * `fetch` with a deadline, normalising every failure-to-answer into
 * `NetworkError`.
 *
 * If a caller supplies its own `signal` it owns the lifetime and we do not
 * impose a deadline on top — no public `api.*` method does this today.
 */
async function fetchWithDeadline(input: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const callerOwnsLifetime = !!init.signal;
    try {
        return await fetch(input, {
            ...init,
            signal: init.signal ?? AbortSignal.timeout(timeoutMs),
        });
    } catch (err) {
        // A caller's own abort is their business — surface it untouched.
        if (callerOwnsLifetime) throw err;
        if (err instanceof DOMException && err.name === "TimeoutError") {
            throw new NetworkError(`The server did not respond within ${Math.round(timeoutMs / 1000)}s.`, true);
        }
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        throw new NetworkError("Could not reach the server.", false);
    }
}

/* ─── Session expiry — one place, not 1,452 ──────────────────────────────────
 *
 * Before this, a lapsed session produced a raw "Invalid or expired token" toast
 * (auth/middleware.ts) and nothing else. Worse than the toast: every component
 * that falls back on failure then rendered its default as though it were an
 * answer. The Money → QuickBooks card showed push mode "Off", "server autopush
 * off", "0 invoices" and "every finalized invoice is already in QuickBooks" —
 * four invented statements on a money screen, because its queries had 401'd and
 * it only ever checked `isLoading`. See threads/cycle-1-believable/ §D4.
 *
 * `auth.tsx` registers the handler (it imports us, so we must not import it).
 */

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Registered once by AuthProvider. Returns an unsubscribe. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): () => void {
    unauthorizedHandler = handler;
    return () => {
        if (unauthorizedHandler === handler) unauthorizedHandler = null;
    };
}

/** Where to send the user back after they sign in again. */
const RETURN_PATH_KEY = "bf_return_path";
/** Set so the login screen can explain itself rather than looking like a bug. */
const SESSION_EXPIRED_KEY = "bf_session_expired";

/**
 * Remember a deep link before bouncing a signed-out visitor to /login, so the
 * link in a text message (e.g. a call-in alert) still lands on the right page
 * after they sign in on a phone that had no session yet.
 */
export function rememberReturnPath(path: string): void {
    try {
        if (path && path !== "/" && !path.startsWith("/login")) sessionStorage.setItem(RETURN_PATH_KEY, path);
    } catch {
        /* private mode — signing in still works, it just lands on home */
    }
}

/** Read the remembered path WITHOUT clearing it. Safe to call during render:
 *  StrictMode renders twice, and a clearing read would lose it on the first. */
export function peekReturnPath(): string | null {
    try {
        return sessionStorage.getItem(RETURN_PATH_KEY);
    } catch {
        return null;
    }
}

export function takeReturnPath(): string | null {
    const v = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return v;
}

export function takeSessionExpired(): boolean {
    const v = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return v;
}

/**
 * A 401 from these is an *answer*, not a dead session: signing in with a bad
 * password, or booting with no token at all. Bouncing on those would put the
 * login page into a redirect loop and would blank the "wrong password" message.
 * The customer portal keeps its own session entirely (`lib/portal-auth.tsx`).
 */
function isAuthExempt(path: string): boolean {
    return (
        path.startsWith("/api/auth/login") ||
        path.startsWith("/api/auth/google") ||
        path.startsWith("/api/auth/me") ||
        path.startsWith("/api/portal/")
    );
}

/** Debounced: a page with eight queries in flight must bounce once, not eight times. */
let handlingUnauthorized = false;

function noteUnauthorized(path: string): void {
    // No token means "not signed in", which is normal and not an expiry.
    if (isAuthExempt(path) || !localStorage.getItem(TOKEN_KEY)) return;
    if (handlingUnauthorized) return;
    handlingUnauthorized = true;
    try {
        const here = `${window.location.pathname}${window.location.search}`;
        if (!here.startsWith("/login")) sessionStorage.setItem(RETURN_PATH_KEY, here);
        sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
    } catch {
        /* private mode — the redirect still matters more than the niceties */
    }
    unauthorizedHandler?.();
    // Let a later, genuinely new expiry bounce again.
    setTimeout(() => {
        handlingUnauthorized = false;
    }, 2_000);
}

/**
 * Per-call options. `timeoutMs` overrides `REQUEST_TIMEOUT_MS` for one request
 * — for an endpoint whose honest server-side cost exceeds the default deadline.
 * Cutting such a call off does not make it faster; it just tells the user it
 * failed while the server finishes successfully (Issue #1421). Use sparingly,
 * and only where the caller can also cope with a genuine timeout.
 */
export interface RequestOptions {
    timeoutMs?: number;
}

/** Job page v2 (slice 6): say which screen the call came from, for the audit log. */
function setScreenHeader(headers: Headers): void {
    const screen = currentApiScreen();
    if (screen && !headers.has("X-GWC-Screen")) headers.set("X-GWC-Screen", screen);
}

async function request<T>(path: string, init: RequestInit = {}, opts: RequestOptions = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
    Object.entries(authHeaders()).forEach(([k, v]) => {
        if (!headers.has(k)) headers.set(k, v);
    });
    setScreenHeader(headers);
    const res = await fetchWithDeadline(url(path), { ...init, headers }, opts.timeoutMs ?? REQUEST_TIMEOUT_MS);
    const text = await res.text();
    let body: unknown = text;
    try {
        body = JSON.parse(text);
    } catch {
        /* raw text */
    }
    if (!res.ok) {
        if (res.status === 401) noteUnauthorized(path);
        throw new ApiError(res.status, res.statusText, body);
    }
    return body as T;
}

/** Multipart upload helper (e.g. file upload). Does not set Content-Type so the browser sets multipart boundary. */
async function multipartRequest<T>(method: "POST" | "PUT", path: string, formData: FormData): Promise<T> {
    const headers = new Headers();
    Object.entries(authHeaders()).forEach(([k, v]) => headers.set(k, v));
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    setScreenHeader(headers);
    const res = await fetchWithDeadline(url(path), { method, body: formData, headers }, UPLOAD_TIMEOUT_MS);
    const text = await res.text();
    let body: unknown = text;
    try {
        body = JSON.parse(text);
    } catch {
        /* raw */
    }
    if (!res.ok) {
        if (res.status === 401) noteUnauthorized(path);
        throw new ApiError(res.status, res.statusText, body);
    }
    return body as T;
}

async function postForm<T>(path: string, formData: FormData): Promise<T> {
    return multipartRequest<T>("POST", path, formData);
}

async function putForm<T>(path: string, formData: FormData): Promise<T> {
    return multipartRequest<T>("PUT", path, formData);
}

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, data?: unknown, opts?: RequestOptions) =>
        request<T>(
            path,
            {
                method: "POST",
                body: data ? JSON.stringify(data) : undefined,
            },
            opts,
        ),
    postForm,
    putForm,
    patch: <T>(path: string, data?: unknown) =>
        request<T>(path, {
            method: "PATCH",
            body: data ? JSON.stringify(data) : undefined,
        }),
    put: <T>(path: string, data?: unknown) =>
        request<T>(path, {
            method: "PUT",
            body: data ? JSON.stringify(data) : undefined,
        }),
    delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

/** Prefix a path with the configured API origin (drag-out URLs, etc.). */
export function apiUrl(path: string): string {
    return url(path);
}

export function setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    // Mirror into IndexedDB so the service worker's Background Sync
    // drain can replay queued mutations with the correct
    // Authorization header. Imported lazily + fire-and-forget so a
    // missing/broken IDB never blocks the login redirect.
    void import("./offline-sync").then((m) => m.mirrorAuthTokenForSw(token)).catch(() => {});
}

export function clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    void import("./offline-sync").then((m) => m.mirrorAuthTokenForSw(null)).catch(() => {});
}

/**
 * Build a full URL for the attachment download endpoint with the JWT
 * appended as `?token=...`. New tabs (target=_blank) and direct
 * browser navigation can't carry a Bearer header — they only have
 * the URL — so the server-side honorQueryToken shim picks the token
 * out of the query string for this one endpoint.
 *
 * Returns the bare path when no token is present (callers should
 * still render a link; the server will 401 cleanly rather than
 * leaking a "token=" in the URL).
 */
export function attachmentDownloadUrl(
    id: string,
    opts: { disposition?: "attachment" | "inline"; variant?: "thumb" } = {},
): string {
    const token = getToken();
    const params = new URLSearchParams();
    if (opts.disposition) params.set("disposition", opts.disposition);
    if (opts.variant) params.set("variant", opts.variant);
    if (token) params.set("token", token);
    const qs = params.toString();
    return `${url(`/api/attachments/${id}/download`)}${qs ? `?${qs}` : ""}`;
}

/** URL for the bulk zip endpoint that bundles the given attachment ids. */
export function attachmentsZipUrl(ids: string[]): string {
    const token = getToken();
    const params = new URLSearchParams();
    params.set("ids", ids.join(","));
    if (token) params.set("token", token);
    return `${url("/api/attachments/zip")}?${params.toString()}`;
}

/**
 * Fetch an attachment as a blob and trigger a real "save file" download with
 * the server-provided filename (no new tab). Falls back to the file_name hint
 * if the Content-Disposition header is absent.
 */
export async function downloadAttachmentFile(id: string, fallbackName?: string): Promise<void> {
    const token = getToken();
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(url(`/api/attachments/${id}/download?disposition=attachment`), { headers });
    if (!res.ok) throw new ApiError(res.status, res.statusText, await res.text().catch(() => null));
    const blob = await res.blob();
    let filename = fallbackName ?? `attachment-${id.slice(0, 8)}`;
    const disposition = res.headers.get("Content-Disposition");
    if (disposition) {
        const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (star) filename = decodeURIComponent(star[1]);
        else if (plain) filename = plain[1];
    }
    await triggerBlobDownload(blob, filename);
}

/**
 * Fetch an authenticated API path (Bearer header) as a blob and trigger a
 * "save file" download, honoring the server's Content-Disposition filename.
 * Use for report/export endpoints (payroll CSV/PDF, etc.) that a plain
 * `window.open` can't reach because they require the Authorization header.
 */
export async function downloadAuthedFile(path: string, fallbackName: string): Promise<void> {
    const headers = new Headers();
    Object.entries(authHeaders()).forEach(([k, v]) => headers.set(k, v));
    const res = await fetch(url(path), { headers });
    if (!res.ok) throw new ApiError(res.status, res.statusText, await res.text().catch(() => null));
    const blob = await res.blob();
    let filename = fallbackName;
    const disposition = res.headers.get("Content-Disposition");
    if (disposition) {
        const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (star) filename = decodeURIComponent(star[1]);
        else if (plain) filename = plain[1];
    }
    await triggerBlobDownload(blob, filename);
}

/**
 * Fetch an authenticated API path (Bearer header) as a Blob. Use for inline
 * previews (PDF/image) where the endpoint requires auth and a plain URL in an
 * <img>/<iframe> src can't carry the Authorization header.
 */
export async function fetchAuthedBlob(path: string): Promise<Blob> {
    const headers = new Headers();
    Object.entries(authHeaders()).forEach(([k, v]) => headers.set(k, v));
    const res = await fetch(url(path), { headers });
    if (!res.ok) throw new ApiError(res.status, res.statusText, await res.text().catch(() => null));
    return res.blob();
}

/** Download a URL (e.g. the zip endpoint) as a blob with a given filename. */
export async function downloadUrlAsFile(downloadUrl: string, fallbackName: string): Promise<void> {
    const res = await fetch(downloadUrl);
    if (!res.ok) throw new ApiError(res.status, res.statusText, await res.text().catch(() => null));
    const blob = await res.blob();
    let filename = fallbackName;
    const disposition = res.headers.get("Content-Disposition");
    if (disposition) {
        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (plain) filename = plain[1];
    }
    await triggerBlobDownload(blob, filename);
}

async function triggerBlobDownload(blob: Blob, filename: string): Promise<void> {
    if (isStandalone() && typeof navigator.share === "function" && navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type || "application/octet-stream" });
        if (navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({ files: [file], title: filename });
                return;
            } catch {
                /* user cancelled or share unavailable — fall through */
            }
        }
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
